// The command outbox (plan §9.3). A station action appends its command to `outbox_command`
// in the same local transaction as its effect; the engine's upload queue is the outbox, FIFO
// per device. Every engine's `enqueueCommand` is `enqueueInTx` inside `writeTransaction`, so
// callers that must number a record first can do both in one transaction themselves:
//
//   await engine.writeTransaction(async (tx) => {
//     const code = await nextDocNumber(tx, deviceId, 'L');
//     await enqueueInTx(tx, draftWith(code), effect);
//   });

import { CORE_COMMANDS, type CommandEnvelope, CommandRegistry, commandEnvelope } from '@enp/domain';
import type {
  CommandDraft,
  CommandDraftInTx,
  LocalEffect,
  LocalTx,
  SyncEngine,
  UploadOp,
} from './engine.js';
import { nextDeviceSeq } from './numbering.js';
import { OUTBOX_TABLE } from './schema.js';

/** The commands this station build understands (the same registry the server runs). */
export const STATION_REGISTRY = new CommandRegistry(CORE_COMMANDS);

/** A command that would never apply: it is refused before anything is written. */
export class InvalidCommandError extends Error {
  constructor(
    readonly reason: string,
    readonly issues: { path: string; message: string }[],
  ) {
    super(`Invalid command (${reason}): ${issues.map((i) => `${i.path} ${i.message}`).join('; ')}`);
    this.name = 'InvalidCommandError';
  }
}

/**
 * Appends a command to the outbox and runs its local effect in the caller's transaction. A
 * draft given as a function is made inside the transaction (it may take document numbers);
 * if it throws or the command is invalid, the transaction rolls back and nothing is kept.
 */
export async function enqueueInTx(
  tx: LocalTx,
  draftOrMake: CommandDraft | CommandDraftInTx,
  localEffect?: LocalEffect,
  registry: CommandRegistry = STATION_REGISTRY,
): Promise<CommandEnvelope> {
  const draft = typeof draftOrMake === 'function' ? await draftOrMake(tx) : draftOrMake;
  // Validate with a placeholder seq first, so nothing is numbered for a command we refuse.
  const check = registry.validate({ ...draft, device_seq: 1 });
  if (!check.ok) throw new InvalidCommandError(check.reason, check.issues);
  const { seq } = await nextDeviceSeq(tx, draft.device_id);
  const envelope = commandEnvelope.parse({ ...draft, device_seq: seq });
  await tx.execute(
    `INSERT INTO ${OUTBOX_TABLE} (id, type, schema_version, payload, device_id, staff_id, device_seq, occurred_at, app_version)
     VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`,
    [
      envelope.command_id,
      envelope.type,
      envelope.schema_version,
      JSON.stringify(envelope.payload ?? null),
      envelope.device_id,
      envelope.staff_id,
      envelope.device_seq,
      envelope.occurred_at,
      envelope.app_version,
    ],
  );
  if (localEffect) await localEffect(tx, envelope);
  return envelope;
}

/** `enqueueCommand` for any engine: `enqueueInTx` in a fresh write transaction. */
export function enqueueCommand(
  engine: Pick<SyncEngine, 'writeTransaction'>,
  draft: CommandDraft | CommandDraftInTx,
  localEffect?: LocalEffect,
): Promise<CommandEnvelope> {
  return engine.writeTransaction((tx) => enqueueInTx(tx, draft, localEffect));
}

/** True for a queued outbox row (the other queued changes are optimistic local effects). */
export function isOutboxOp(op: UploadOp): boolean {
  return op.table === OUTBOX_TABLE && op.op === 'PUT';
}

/**
 * An outbox row back to the envelope that is sent. A row that no longer parses (it should
 * never happen: rows are validated before they are written) is sent as it is, so the server
 * holds it for a manager (`invalid_envelope`) instead of it wedging the outbox.
 */
export function envelopeFromOp(op: UploadOp): CommandEnvelope | Record<string, unknown> {
  const d = op.data ?? {};
  let payload: unknown = d['payload'];
  if (typeof payload === 'string') {
    try {
      payload = JSON.parse(payload);
    } catch {
      // keep the text: the server will hold it
    }
  }
  const raw = {
    command_id: op.id,
    type: d['type'],
    schema_version: Number(d['schema_version']),
    payload,
    device_id: d['device_id'],
    staff_id: d['staff_id'],
    device_seq: Number(d['device_seq']),
    occurred_at: d['occurred_at'],
    app_version: d['app_version'],
  };
  const parsed = commandEnvelope.safeParse(raw);
  return parsed.success ? parsed.data : raw;
}
